/**
 * @module manifest/settingsSchema
 * @description 图表配置项「标量值」的强类型 Schema —— 类型化样板（治理兜底操作符的根因）
 *
 * ## 背景
 *
 * 当前 BASE/DRAFT 以 `Record<string, unknown>` 扁平存储，导致每个读取点都被迫
 * 强转 + 兜底（`(s.draft.yAxisName ?? s.base.yAxisName) as string) || ''`）。
 * 本文件示范一种渐进式治理：为配置项声明「类型 + 默认值」，把
 * `unknown → 具体类型` 的边界收敛到唯一适配层（见 context/useSettingState），
 * 让下游组件直接拿到强类型值，不再写任何兜底。
 *
 * ## 如何扩展
 *
 * - 标量（string/number/boolean）：在 `types/settings/scalars` 扩展 `IScalarSettingsSchema`、
 *   在 `SCALAR_SETTING_DEFAULTS` 给默认值。
 * - 结构化（对象/数组）：在 `types/settings/<field>.ts` 定义形状与 `*_DEFAULT`；
 *   在本文件 `IStructuredSettingsSchema` 加字段、在 `STRUCTURED_SETTING_DEFAULTS` 装配冻结引用。
 * 字段名与上传字段名（upload-keys / groups 声明）保持一致。
 *
 * ## 标量 vs 结构化的关键差异（性能）
 *
 * 标量按值比较，默认值是基本类型，天然稳定。
 * 结构化按引用比较（selector 底层 Object.is）：读取必须「原样返回存储引用」，
 * 缺省时返回「唯一冻结默认引用」——绝不能用 `?? {}` / `?? []` 现造对象，
 * 否则空态下每次求值都得到新引用，破坏 shallowEqual 导致无效重渲染。
 */

import type {
  IChartSwitchValue,
  IColorSettingValue,
  IConditionalFormatValue,
  ICrosstabConfig,
  IDescriptionSetting,
  IDimensionPercentageConfig,
  IFieldSetting,
  IFreezeSetting,
  IMapColorsValue,
  IMetricPercentageItem,
  IReferenceLineItem,
  IScalarSettingsSchema,
  IMetricSortValueItem,
  IStatisticsValue,
  ISeriesSortValueItem,
  IToolTipSettingValue,
  TYAxisAxisStorage,
  TLabelContent,
  TLegendColors,
} from '../types/settings';
import {
  CHART_SORTS_DEFAULT,
  CHART_SWITCH_DEFAULT,
  COLOR_SETTING_DEFAULT,
  CUSTOM_METRIC_SORT_DEFAULT,
  CONDITIONAL_FORMAT_DEFAULT,
  PRIMARY_Y_AXIS_DEFAULT,
  SECOND_Y_AXIS_DEFAULT,
  SERIES_ORDER_DEFAULT,
  SERIES_SORTS_DEFAULT,
  X_AXIS_VALUES_DEFAULT,
  COLUMN_PERCENTAGE_DEFAULT,
  DIMENSION_PERCENTAGE_DEFAULT,
  CROSSTAB_DEFAULT,
  DESCRIPTION_DEFAULT,
  FIELD_SETTING_DEFAULT,
  FREEZE_DEFAULT,
  LABEL_CONTENT_DEFAULT,
  LEGEND_COLORS_DEFAULT,
  MAP_COLORS_DEFAULT,
  REFERENCE_LINE_DEFAULT,
  STATISTICS_DEFAULT,
  TOOLTIP_SETTING_DEFAULT,
} from '../types/settings';
import {
  DEFAULT_STACKED_COUNT,
  DEFAULT_TOPN_COUNT,
  DEFAULT_TOPN_SHOW_OTHERS,
} from '../utils/constants';
import { NULL_UI_DEFAULT } from '@dbl/core/chartEditor';

export type {
  IFreezeSetting,
  IDescriptionSetting,
  IMetricPercentageItem,
  IScalarSettingsSchema,
};

/**
 * 各标量配置项的默认值 —— 缺省语义集中在此处定义，而非散落在每个读取点
 *
 * 标量默认值为基本类型，按值比较恒稳定，天然满足 selector 的引用稳定要求。
 */
export const SCALAR_SETTING_DEFAULTS: IScalarSettingsSchema = {
  yAxisName: '',
  xAxisName: '',
  legend_position: 'below',
  x_label: 'auto',
  displayForm: 'adaptive',
  showDataLabel: false,
  dualYAxisEnabled: false,
  yAxisRangeSwitch: false,
  sort_series: 'default',
  sortBy: '',
  count: DEFAULT_TOPN_COUNT,
  dynamicState: false,
  showOthers: DEFAULT_TOPN_SHOW_OTHERS,
  stackedCount: DEFAULT_STACKED_COUNT,
  stackedDynamicState: false,
  stackedShowOthers: DEFAULT_TOPN_SHOW_OTHERS,
  null_ui: NULL_UI_DEFAULT,
};

/** 标量配置项键名联合类型 */
export type TScalarSettingKey = keyof IScalarSettingsSchema;

/**
 * 结构化（对象/数组）配置项的类型契约 —— key 为存储字段名，value 为完整结构类型
 *
 * 与标量同理：必填 + 具体类型，默认值保证字段恒存在，下游无需判空。
 */
export interface IStructuredSettingsSchema {
  /** 表格字段描述：key=`${table_code}_${column_name}_${column_role}` → 描述项 */
  description: IDescriptionSetting;
  /** 表格冻结窗格：开关 + 冻结列数 */
  freeze: IFreezeSetting;
  /** 列占比勾选列表 */
  column_percentage: IMetricPercentageItem[];
  /** 维度占比：group_by + metrics 嵌套结构（后端契约 dimension_percentage） */
  dimension_percentage: IDimensionPercentageConfig;
  /** 数据标签内容多选（metric / dimension / percent / total） */
  labelContent: TLabelContent;
  /** 表格维度 URL 格式：key=`${table_code}_${column_name}` */
  field_setting: IFieldSetting;
  /** 图表类型切换：show + chartTypes */
  chart_switch: IChartSwitchValue;
  /** 图例颜色：legendKey → rgba */
  legend_colors: TLegendColors;
  /** 地图色阶：colorName + colors */
  map_colors: IMapColorsValue;
  /** 透视表行列：ICrosstabFieldRef[] */
  crosstab: ICrosstabConfig;
  /** 高级参考线列表 */
  reference_line: IReferenceLineItem[];
  /** 浮窗：合计/占比/额外内容 */
  tooltipSetting: IToolTipSettingValue;
  /** 词云颜色：type + colors（可选 colorName） */
  colorSetting: IColorSettingValue;
  /** 表格条件格式：条形图 / 数值条件列表 */
  conditional_format: IConditionalFormatValue;
  /** 表格统计值：avg / avgNotNull / max / min / sum → 列名数组 */
  statistics: IStatisticsValue;
  /** 主轴对象（折线 metrics[] / 组合 metric string） */
  primaryYAxis: TYAxisAxisStorage;
  /** 次轴对象 */
  secondYAxis: TYAxisAxisStorage;
  /** X 轴按指标排序（与 customMetricSort 双写） */
  chartSorts: IMetricSortValueItem[];
  customMetricSort: IMetricSortValueItem[];
  /** X 轴手动排序轴值 */
  xAxisValues: string[];
  /** 图例手动系列顺序 */
  series_order: string[];
  /** 图例按指标排序 */
  series_sorts: ISeriesSortValueItem[];
}

/** 结构化配置项键名联合类型 */
export type TStructuredSettingKey = keyof IStructuredSettingsSchema;

/**
 * 各结构化配置项的默认值 —— 必须是「模块级唯一冻结引用」
 *
 * @stable 空态下 selector 返回这些引用，Object.is 恒等 → 不触发无效重渲染。
 * 禁止 mutate；禁止在读取点用 `?? {}` / `?? []` 替代（那样每次都是新引用）。
 */
export const STRUCTURED_SETTING_DEFAULTS: IStructuredSettingsSchema = {
  description: DESCRIPTION_DEFAULT,
  freeze: Object.freeze({ ...FREEZE_DEFAULT }) as IFreezeSetting,
  column_percentage: COLUMN_PERCENTAGE_DEFAULT,
  dimension_percentage: DIMENSION_PERCENTAGE_DEFAULT,
  labelContent: LABEL_CONTENT_DEFAULT,
  field_setting: FIELD_SETTING_DEFAULT,
  chart_switch: CHART_SWITCH_DEFAULT,
  legend_colors: LEGEND_COLORS_DEFAULT,
  map_colors: MAP_COLORS_DEFAULT,
  crosstab: CROSSTAB_DEFAULT,
  reference_line: REFERENCE_LINE_DEFAULT,
  tooltipSetting: TOOLTIP_SETTING_DEFAULT,
  colorSetting: COLOR_SETTING_DEFAULT,
  conditional_format: CONDITIONAL_FORMAT_DEFAULT,
  statistics: STATISTICS_DEFAULT,
  primaryYAxis: PRIMARY_Y_AXIS_DEFAULT,
  secondYAxis: SECOND_Y_AXIS_DEFAULT,
  chartSorts: CHART_SORTS_DEFAULT,
  customMetricSort: CUSTOM_METRIC_SORT_DEFAULT,
  xAxisValues: X_AXIS_VALUES_DEFAULT,
  series_order: SERIES_ORDER_DEFAULT,
  series_sorts: SERIES_SORTS_DEFAULT,
};
